import React, { useRef } from "react";
import useDetectClose from "hooks/useDetectClose";
import styled from "styled-components";
import { FlexContainer, Button } from "styles/sharedStyles";
import { ReactComponent as ArrowIcon } from "@/assets/dropdown/arrow_icon.svg?react";
import { designTokens, getTypography } from "styles/designTokens";

interface DropDownProps {
  options: string[];
  selectedOption: string;
  onSelect: (option: string) => void;
  width?: string;
}

interface ArrowProps {
  $isOpen: boolean;
}

const DropDownContainer = styled(FlexContainer)<{ $width?: string }>`
  position: relative;
  z-index: 1;
`;

const DropDownButton = styled(Button)`
  border: 1px solid ${designTokens.colors.font.tertiary};
  border-radius: 6px;
  height: 42px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #ffffff;
  font-size: ${getTypography("korean", "body3").fontSize};
  font-weight: ${getTypography("korean", "body3").fontWeight};
  line-height: ${getTypography("korean", "body3").lineHeight};
  letter-spacing: ${getTypography("korean", "body3").letterSpacing};
  color: ${designTokens.colors.font.tertiary};

  &:hover {
    background: #f9f9f9;
  }
`;

const DropDownMenu = styled.ul<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid ${designTokens.colors.font.tertiary};
  border-radius: 6px;
  padding: 0;
  margin: 10px 0 0 0;
  list-style: none;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
  transform: ${({ $isOpen }) =>
    $isOpen ? "translateY(0)" : "translateY(-20px)"};
  transition: opacity 0.4s ease, transform 0.4s ease, visibility 0.4s;
  z-index: 2;
`;

const MenuItem = styled.li`
  padding: 8px 16px;
  font-size: ${getTypography("korean", "body3").fontSize};
  font-weight: ${getTypography("korean", "body3").fontWeight};
  line-height: ${getTypography("korean", "body3").lineHeight};
  letter-spacing: ${getTypography("korean", "body3").letterSpacing};
  color: ${designTokens.colors.font.tertiary};
  cursor: pointer;

  &:hover {
    background-color: #e0e7ff;
  }
`;

const StyledArrowIcon = styled(ArrowIcon)<ArrowProps>`
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.3s ease;
`;

const DropDown: React.FC<DropDownProps> = ({
  options,
  selectedOption,
  onSelect,
  width,
}) => {
  const dropDownRef = useRef<HTMLUListElement>(null);
  const [isOpen, setIsOpen] = useDetectClose(dropDownRef as React.RefObject<HTMLElement>, false);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: string) => {
    onSelect(option);
    setIsOpen(false);
  };

  const sortedOptions = React.useMemo(() => {
    const allOption = options.find((opt) => opt === "전체");
    const otherOptions = options.filter((opt) => opt !== "전체");

    otherOptions.sort((a, b) => a.localeCompare(b, "ko"));

    return allOption ? [allOption, ...otherOptions] : otherOptions;
  }, [options]);

  return (
    <DropDownContainer $width={width}>
      <DropDownButton onClick={handleButtonClick}>
        {selectedOption}
        <StyledArrowIcon $isOpen={isOpen} />
      </DropDownButton>
      <DropDownMenu ref={dropDownRef} $isOpen={isOpen}>
        {sortedOptions.map((option, index) => (
          <MenuItem key={index} onClick={() => handleOptionClick(option)}>
            {option}
          </MenuItem>
        ))}
      </DropDownMenu>
    </DropDownContainer>
  );
};

export default DropDown;
